// src/pages/TutorChatPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { FaArrowLeft, FaPen, FaPaperPlane } from "react-icons/fa";
import { fetchTutorStatus } from "../utils/premiumService";
import { saveTutorGoal, sendTutorMessage } from "../utils/tutorService";
import { matchCanned, pickLine, fillLine } from "../utils/tutorLines";
import mascotImg from "../assets/App-logo-final.png";
import "./TutorChatPage.css";

const GOAL_EXAMPLES = [
  "Crack my exams",
  "Become an engineer",
  "Become a doctor",
  "Learn science for fun",
];

// The AI sometimes uses markdown. Show it as plain, clean text.
const cleanReply = (text) =>
  text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .trim();

const TutorChatPage = () => {
  const navigate = useNavigate();
  const firstName = useMemo(() => {
    const n = (getAuth().currentUser?.displayName || "").split(" ")[0];
    return n || "friend";
  }, []);

  const [phase, setPhase] = useState("loading"); // loading | ready | failed
  const [goal, setGoal] = useState("");
  const [daysLeft, setDaysLeft] = useState(30);
  const [remaining, setRemaining] = useState(null);

  const [editingGoal, setEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalError, setGoalError] = useState("");

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [limitHit, setLimitHit] = useState(false);
  const [aha, setAha] = useState(false);

  const idRef = useRef(0);
  const didInit = useRef(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const ahaTimer = useRef(null);

  const addMsg = (role, content, extra = {}) =>
    setMessages((prev) => [...prev, { id: ++idRef.current, role, content, ...extra }]);

  // ---- Load status, gate on premium ----
  const loadStatus = async () => {
    setPhase("loading");
    try {
      const s = await fetchTutorStatus();
      if (s.status !== "active") {
        navigate("/tutor/premium", { replace: true });
        return;
      }
      setGoal(s.goal || "");
      setDaysLeft(s.daysLeft);
      setPhase("ready");
      if (!didInit.current) {
        didInit.current = true;
        if (s.goal) {
          const line = fillLine(pickLine("welcome"), { name: firstName });
          addMsg("assistant", `${line} We're working toward: ${s.goal}.`);
        } else {
          setGoalDraft("");
          setEditingGoal(true); // first time: ask for the dream
        }
      }
    } catch (e) {
      console.error("Could not load tutor status:", e);
      setPhase("failed");
    }
  };

  useEffect(() => {
    loadStatus();
    return () => clearTimeout(ahaTimer.current);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  // ---- Goal ----
  const startEditGoal = () => {
    setGoalDraft(goal);
    setGoalError("");
    setEditingGoal(true);
  };

  const handleSaveGoal = async () => {
    const clean = goalDraft.replace(/\s+/g, " ").trim().slice(0, 100);
    if (!clean || savingGoal) return;
    setSavingGoal(true);
    setGoalError("");
    try {
      await saveTutorGoal(clean);
      const first = !goal;
      setGoal(clean);
      setEditingGoal(false);
      addMsg(
        "assistant",
        fillLine(pickLine(first ? "goalSaved" : "goalChanged"), { name: firstName, goal: clean })
      );
    } catch (e) {
      console.error("Could not save goal:", e);
      setGoalError("We couldn't save your goal. Please try again.");
    } finally {
      setSavingGoal(false);
    }
  };

  // ---- Chat ----
  const resizeInput = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const popAha = () => {
    setAha(true);
    clearTimeout(ahaTimer.current);
    ahaTimer.current = setTimeout(() => setAha(false), 2600);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || limitHit) return;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";

    const userMsg = { id: ++idRef.current, role: "user", content: text };

    // 1. Greetings, thanks and "ok" are answered locally. No AI call, no cost.
    const canned = matchCanned(text);
    if (canned) {
      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: ++idRef.current,
          role: "assistant",
          content: fillLine(pickLine(canned), { name: firstName }),
        },
      ]);
      return;
    }

    // 2. Everything else goes to the tutor
    const history = [...messages, userMsg]
      .filter((m) => !m.local)
      .map((m) => ({ role: m.role, content: m.content }))
      .slice(-10);

    setMessages((prev) => [...prev, userMsg]);
    setSending(true);

    try {
      const res = await sendTutorMessage(history);
      let reply = res.reply || "";
      const isAha = /^\s*\[aha\]/i.test(reply);
      reply = cleanReply(reply.replace(/^\s*\[aha\]\s*/i, ""));
      addMsg("assistant", reply);
      if (typeof res.remainingToday === "number") setRemaining(res.remainingToday);
      if (typeof res.daysLeft === "number") setDaysLeft(res.daysLeft);
      if (isAha) popAha();
    } catch (err) {
      const msg = err?.message || "";
      if (msg === "premium_expired" || msg === "premium_required") {
        navigate("/tutor/premium", { replace: true });
        return;
      }
      if (msg === "daily_limit") {
        setLimitHit(true);
        addMsg("assistant", fillLine(pickLine("cap"), { name: firstName }), { local: true });
        return;
      }
      console.error("Tutor chat failed:", err);
      addMsg("assistant", pickLine("error"), { local: true, error: true });
      setInput((prev) => prev || text); // give the message back so they can resend
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ---- Screens ----
  if (phase === "loading") {
    return (
      <div className="tc-page tc-center">
        <img src={mascotImg} alt="" className="tc-mascot tc-big tc-bounce" />
        <p className="tc-muted">Waking up your octopus...</p>
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div className="tc-page tc-center">
        <img src={mascotImg} alt="" className="tc-mascot tc-big" />
        <p className="tc-muted">We couldn't reach your octopus. Check your connection.</p>
        <button className="tc-btn tc-narrow" onClick={loadStatus}>
          Try again
        </button>
        <button className="tc-link" onClick={() => navigate("/HomeScreen")}>
          Back to home
        </button>
      </div>
    );
  }

  if (editingGoal) {
    return (
      <div className="tc-page">
        <header className="tc-header">
          <button
            className="tc-iconbtn"
            aria-label="Go back"
            onClick={() => (goal ? setEditingGoal(false) : navigate("/HomeScreen"))}
          >
            <FaArrowLeft />
          </button>
        </header>
        <div className="tc-goal">
          <img src={mascotImg} alt="" className="tc-mascot tc-big tc-bounce" />
          <h1 className="tc-h1">{goal ? "Change your goal" : "What's your dream?"}</h1>
          <p className="tc-muted">
            {goal
              ? "Changing your goal? I'll help with the new one."
              : "Tell me the goal you're working toward. I'll keep it in mind as we learn."}
          </p>
          <input
            className="tc-goalinput"
            value={goalDraft}
            onChange={(e) => setGoalDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSaveGoal()}
            maxLength={100}
            placeholder="For example: become a doctor"
            aria-label="Your goal"
          />
          <div className="tc-counter">{goalDraft.length}/100</div>
          <div className="tc-chips">
            {GOAL_EXAMPLES.map((g) => (
              <button key={g} className="tc-chip" onClick={() => setGoalDraft(g)}>
                {g}
              </button>
            ))}
          </div>
          {goalError && <p className="tc-error">{goalError}</p>}
          <button
            className="tc-btn"
            onClick={handleSaveGoal}
            disabled={!goalDraft.trim() || savingGoal}
          >
            {savingGoal ? "Saving..." : "Save my goal"}
          </button>
          {goal && (
            <button className="tc-link" onClick={() => setEditingGoal(false)}>
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tc-page">
      <header className="tc-header">
        <button className="tc-iconbtn" aria-label="Back to home" onClick={() => navigate("/HomeScreen")}>
          <FaArrowLeft />
        </button>
        <div className="tc-avatar">
          <img src={mascotImg} alt="" className={sending ? "tc-mascot tc-bounce" : "tc-mascot"} />
          {aha && (
            <span className="tc-aha" aria-hidden="true">
              !
            </span>
          )}
        </div>
        <div className="tc-titles">
          <div className="tc-title">Your octopus buddy</div>
          <div className="tc-goaltext">{goal}</div>
        </div>
        <button className="tc-change" onClick={startEditGoal}>
          <FaPen aria-hidden="true" />
          <span>Change goal</span>
        </button>
      </header>

      {daysLeft <= 3 && (
        <div className="tc-banner">
          Your plan ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}.{" "}
          <button className="tc-inline" onClick={() => navigate("/tutor/premium?renew=1")}>
            Renew anytime
          </button>
        </div>
      )}

      <div className="tc-list" role="log" aria-live="polite">
        {messages.map((m) => (
          <div key={m.id} className={`tc-row ${m.role}`}>
            <div className={`tc-bubble ${m.role}${m.error ? " error" : ""}`}>{m.content}</div>
          </div>
        ))}
        {sending && (
          <div className="tc-row assistant">
            <div className="tc-bubble assistant tc-typing" aria-label="Your octopus is thinking">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {remaining !== null && remaining <= 10 && !limitHit && (
        <p className="tc-remaining">
          {remaining} message{remaining === 1 ? "" : "s"} left today
        </p>
      )}

      <div className="tc-inputbar">
        <textarea
          ref={inputRef}
          className="tc-input"
          rows={1}
          value={input}
          maxLength={1000}
          disabled={limitHit}
          placeholder={limitHit ? "See you tomorrow!" : "Ask your octopus anything..."}
          aria-label="Your message"
          onChange={(e) => {
            setInput(e.target.value);
            resizeInput();
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          className="tc-send"
          aria-label="Send"
          onClick={handleSend}
          disabled={!input.trim() || sending || limitHit}
        >
          <FaPaperPlane />
        </button>
      </div>
    </div>
  );
};

export default TutorChatPage;